'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { LucideIcon } from '@/components/LucideIcon';
import { RichText } from '@payloadcms/richtext-lexical/react';

function Desc({ data, className, style }: { data: any; className?: string; style?: React.CSSProperties }) {
  if (!data) return null;
  if (typeof data === 'string') return <p className={className} style={style}>{data}</p>;
  return <div className={className} style={style}><RichText data={data} /></div>;
}

export function FeatureShowcaseBlockRenderer({ block }: { block: any }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const features = block.features || [];
  const imgLeft = block.imagePosition === 'left';

  // Track whether the user is manually hovering/clicking so scroll doesn't fight it
  const manualRef = useRef(false);
  const manualTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const featureRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleManualActivate = useCallback((i: number) => {
    setActiveIdx(i);
    manualRef.current = true;
    if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    // Resume scroll-driven behaviour after 2s of inactivity
    manualTimerRef.current = setTimeout(() => { manualRef.current = false; }, 2000);
  }, []);

  // IntersectionObserver: auto-highlight whichever feature is most in view
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    featureRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !manualRef.current) {
            setActiveIdx(i);
          }
        },
        { threshold: 0.6 },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [features.length]);

  if (features.length === 0) return null;

  const isAlt = ((block._sectionIndex ?? 0) % 2) === 1;
  const subheadingWidthClass = ({ narrow: 'max-w-xl', medium: 'max-w-3xl', wide: 'max-w-5xl', full: 'max-w-none' } as Record<string, string>)[block.subheadingWidth] ?? 'max-w-3xl';

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: isAlt ? 'var(--color-t-section-alt)' : 'var(--color-t-section-light)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        {block.heading && (
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3" style={{ color: 'var(--color-t-heading)' }}>
              {block.heading}
            </h2>
            {block.subheading && (
              <Desc data={block.subheading} className={`text-base sm:text-lg ${subheadingWidthClass} mx-auto`} style={{ color: 'var(--color-t-body)' }} />
            )}
          </div>
        )}
        {/* Desktop (lg+): sticky screenshot that cross-fades between features as you
            hover or scroll. Hidden on mobile — replaced by the stacked layout below. */}
        <div data-testid="feature-showcase-desktop" className={`hidden lg:grid lg:grid-cols-2 gap-12 items-start ${imgLeft ? '' : 'lg:flex-row-reverse'}`}>
          {/* Screenshot side — sticky so it stays in viewport while user scrolls features */}
          <div className={`lg:sticky lg:top-24 ${imgLeft ? 'lg:order-1' : 'lg:order-2'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-50 aspect-[4/3]">
              {features.map((f: any, i: number) => (
                f.screenshot?.url && (
                  <Image
                    key={i}
                    src={f.screenshot.url}
                    alt={f.title}
                    fill
                    className={`object-cover transition-opacity duration-500 ${i === activeIdx ? 'opacity-100' : 'opacity-0'}`}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    // No `priority`: this is a below-the-fold section (pages lead with
                    // a Hero). Eager-preloading it competed with the real LCP image.
                    loading="lazy"
                  />
                )
              ))}
            </div>
            {/* Dot indicators */}
            {features.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {features.map((_: any, i: number) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to feature ${i + 1}`}
                    onClick={() => handleManualActivate(i)}
                    className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: i === activeIdx ? 'var(--color-t-primary)' : 'color-mix(in srgb, var(--color-t-primary) 25%, transparent)',
                      transform: i === activeIdx ? 'scale(1.4)' : 'scale(1)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Feature list side */}
          <div className={`flex flex-col gap-2 ${imgLeft ? 'lg:order-2' : 'lg:order-1'}`}>
            {features.map((f: any, i: number) => (
              <button
                key={i}
                ref={el => { featureRefs.current[i] = el; }}
                type="button"
                className={`text-left p-5 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                  i === activeIdx ? 'shadow-md' : 'border-transparent hover:border-gray-200'
                }`}
                style={i === activeIdx ? {
                  borderColor: 'var(--color-t-primary)',
                  backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 4%, transparent)',
                } : undefined}
                onMouseEnter={() => handleManualActivate(i)}
                onClick={() => handleManualActivate(i)}
              >
                <div className="flex items-start gap-3">
                  {f.icon && (
                    <span
                      className="shrink-0 mt-0.5 transition-colors duration-300"
                      style={{ color: i === activeIdx ? 'var(--color-t-primary)' : 'var(--color-t-body)' }}
                    >
                      <LucideIcon name={f.icon} size={24} />
                    </span>
                  )}
                  <div>
                    <h3 className="text-base font-bold mb-1" style={{ color: i === activeIdx ? 'var(--color-t-primary)' : 'var(--color-t-heading)' }}>
                      {f.title}
                    </h3>
                    <Desc data={f.description} className="text-sm leading-relaxed" style={{ color: 'var(--color-t-body)' }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Mobile (< lg): the cross-fade can't work in a single scroll column — the
            shared image scrolls off-screen away from its text. Stack each feature
            directly above its own screenshot so copy and image stay together. */}
        <div data-testid="feature-showcase-mobile" className="lg:hidden flex flex-col gap-10">
          {features.map((f: any, i: number) => (
            <div key={i} className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                {f.icon && (
                  <span className="shrink-0 mt-0.5" style={{ color: 'var(--color-t-primary)' }}>
                    <LucideIcon name={f.icon} size={24} />
                  </span>
                )}
                <div>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--color-t-heading)' }}>
                    {f.title}
                  </h3>
                  <Desc data={f.description} className="text-sm leading-relaxed" style={{ color: 'var(--color-t-body)' }} />
                </div>
              </div>
              {f.screenshot?.url && (
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-50 aspect-[4/3]">
                  <Image
                    src={f.screenshot.url}
                    alt={f.title}
                    fill
                    className="object-cover"
                    sizes="100vw"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
